import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        if (!sessionIdMatch) {
          setError({
            title: 'Login failed — no session received',
            details: 'The Google authentication did not return a session. This sometimes happens on mobile browsers if the redirect was blocked or the URL fragment was stripped. Try again, or use a different browser (Chrome).',
            raw: `hash: ${hash || '(empty)'}, url: ${window.location.href}`,
          });
          return;
        }

        const sessionId = sessionIdMatch[1];

        const response = await axios.post(
          `${API}/auth/google/callback`,
          { session_id: sessionId }
        );

        const userData = response.data;

        if (userData.token) {
          localStorage.setItem('auth_token', userData.token);
        }

        const returnUrl = sessionStorage.getItem('auth_return_url') || '/';
        sessionStorage.removeItem('auth_return_url');

        navigate(returnUrl, {
          replace: true,
          state: { user: userData.user },
        });
      } catch (err) {
        const detail = err.response?.data?.detail;
        const sessionLen = (location.hash.match(/session_id=([^&]+)/) || [null, ''])[1].length;
        setError({
          title: 'Login failed',
          details:
            typeof detail === 'string'
              ? detail
              : 'The server could not complete your Google sign-in.',
          raw:
            `status: ${err.response?.status || 'no-response'}\n` +
            `backend detail: ${typeof detail === 'string' ? detail : JSON.stringify(detail) || '(none)'}\n` +
            `axios message: ${err.message}\n` +
            `session_id length: ${sessionLen}\n` +
            `current url: ${window.location.href}`,
        });
      }
    };

    processAuth();
  }, [navigate, location]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="auth-callback-error">
          <div className="text-5xl mb-3">⚠️</div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">
            {error.title}
          </h1>
          <p className="text-stone-600 mb-4">{error.details}</p>
          <details className="text-xs text-stone-500 bg-stone-50 rounded-lg p-3 text-left mb-6" open>
            <summary className="cursor-pointer font-medium">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-all">{error.raw}</pre>
          </details>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-6 py-2 rounded-full bg-[#8D6E63] text-white hover:bg-[#8D6E63]/90"
              data-testid="auth-callback-back-login"
            >
              Back to login
            </button>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="px-6 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100"
            >
              Continue as guest
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#8D6E63] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-stone-600 font-['Poppins']">Signing you in...</p>
      </div>
    </div>
  );
}
